import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClips } from "../../redux/slices/clipsSlice";
import Pagination from "../../components/Common/Pagination";
import Avatar from "../../components/Common/Avatar";
import { Link } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  EyeIcon
} from "@heroicons/react/24/outline";

function Clips() {
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  const { clips, loading, error, total, totalPages, currentPage } = useSelector((state) => state.clips);
  
  const ITEMS_PER_PAGE = 10;

  // Fetch clips when component mounts
  useEffect(() => {
    dispatch(fetchClips({ 
      currentPage: 1,
      limit: ITEMS_PER_PAGE
    }));
  }, [dispatch]);

  // Handle pagination
  const handlePageChange = (page) => {
    dispatch(fetchClips({ 
      currentPage: page,
      limit: ITEMS_PER_PAGE
    }));
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  // Get status badge color
  const getStatusBadge = (clipStatus) => {
    switch (clipStatus?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status dot color
  const getStatusDotColor = (clipStatus) => {
    switch (clipStatus?.toLowerCase()) {
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-gray-400';
      case 'expired':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div>
      <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Dark Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-semibold text-lg">{t("clips.title")}</h2>
            <span className="text-white font-semibold">({total || 0})</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("common.search")}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-28"
              />
            </div>
            
            <button className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light">
              <span>{t("common.sortBy")}</span>
              <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
            </button>

            <button className="flex items-center gap-2 px-3 py-1.5 border border-white rounded text-white text-sm font-light">
              <span>{t("common.dateRange")}</span>
              <ChevronDownIcon className="w-[18px] h-[18px] text-themeLightGray" />
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
            <span className="ml-2 text-dashboardText">{t("clips.loading")}</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        ) : clips.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-dashboardText">{t("clips.noFound")}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dashboardStroke">
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("clips.col.thumbnail")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.user")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("clips.col.userHandle")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("clips.col.description")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("clips.col.price")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("clips.col.views")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("clips.col.uploadDate")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
                    <th className="px-4 py-4 text-left text-[17px] font-medium text-dashboardText">{t("col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {clips && Array.isArray(clips) && clips.map((clip, index) => {
                    const uploadDate = formatDateTime(clip.uploadDate);
                    const expiryDate = formatDateTime(clip.expiryDate);
                    
                    return (
                      <tr 
                        key={clip._id}
                        className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors ${
                          index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                        }`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <img
                            src={clip.thumbnail}
                            alt="Clip thumbnail"
                            className="h-12 w-20 object-cover rounded border"
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Avatar
                              src={clip.userId?.profilePicture}
                              name={clip.userId?.name}
                              size="sm"
                            />
                            <span className="text-dashboardDarkText font-medium">
                              {clip.userId?.name || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          {clip.userId?.userHandle ? `@${clip.userId.userHandle}` : 'N/A'}
                        </td>
                        <td className="px-4 py-4 text-base font-normal text-dashboardDarkText max-w-xs relative group">
                          <div className="truncate cursor-pointer">
                            {clip.description?.length > 50 
                              ? `${clip.description.substring(0, 50)}...` 
                              : (clip.description || 'No description')
                            }
                          </div>
                          {clip.description && clip.description.length > 50 && (
                            <div className="absolute z-50 invisible group-hover:visible bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg max-w-md bottom-full left-0 mb-2 transform">
                              <div className="break-words">{clip.description}</div>
                              <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          ${clip.price || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardDarkText">
                          {clip.viewsCount || 0}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                          <div>{uploadDate.date}</div>
                          <div className="text-xs text-gray-400">{uploadDate.time}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getStatusBadge(clip.status)}`}>
                            <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${getStatusDotColor(clip.status)}`} />
                            {tStatus(clip.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <Link 
                            to={`/users/${typeof clip.userId === 'object' ? clip.userId._id : clip.userId}?tab=clips&clipId=${clip._id}`}
                            className="p-1 hover:bg-gray-100 rounded transition-colors inline-block"
                          >
                            <EyeIcon className="w-6 h-6 text-dashboardDark" strokeWidth={1.3} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-4 border-t border-dashboardStroke">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  total={total}
                  itemsPerPage={ITEMS_PER_PAGE}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Clips;